import React, { useState, useCallback } from 'react';
import { getCurrentMadridISO } from '../utils/dateUtils';

// Helper function to get initial state from localStorage
const getInitialCookieState = () => {
  try {
    const cookieConsent = localStorage.getItem('vivendius_cookie_consent');
    if (cookieConsent) {
      return { 
        showBanner: false, 
        preferences: JSON.parse(cookieConsent) 
      };
    }
  } catch {
    // Ignore errors
  }
  return { 
    showBanner: true, 
    preferences: {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    }
  };
};

const CookiesBanner = () => {
  const initialState = getInitialCookieState();
  
  const [showBanner, setShowBanner] = useState(initialState.showBanner);
  const [showSettings, setShowSettings] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState(initialState.preferences);

  const applyCookiePreferences = useCallback((preferences) => {
    // Apply tracking scripts based on preferences (placeholder)
    if (preferences.analytics) {
      // Initialize Google Analytics if needed
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
      timestamp: getCurrentMadridISO()
    };
    
    localStorage.setItem('vivendius_cookie_consent', JSON.stringify(allAccepted));
    setCookiePreferences(allAccepted);
    applyCookiePreferences(allAccepted);
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
      timestamp: getCurrentMadridISO()
    };
    
    localStorage.setItem('vivendius_cookie_consent', JSON.stringify(onlyNecessary));
    setCookiePreferences(onlyNecessary);
    applyCookiePreferences(onlyNecessary);
    setShowBanner(false);
  };

  const handleCustomSave = () => {
    const customPreferences = {
      ...cookiePreferences,
      timestamp: getCurrentMadridISO()
    };
    
    localStorage.setItem('vivendius_cookie_consent', JSON.stringify(customPreferences));
    applyCookiePreferences(customPreferences);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handlePreferenceChange = (category, value) => {
    if (category === 'necessary') return; // Cannot disable necessary cookies
    
    setCookiePreferences(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const resetCookiePreferences = () => {
    localStorage.removeItem('vivendius_cookie_consent');
    setShowBanner(true);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50 p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-1">
                    🍪 Uso de Cookies
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Utilizamos cookies para mejorar tu experiencia en nuestro sitio web, 
                    personalizar contenido y analizar nuestro tráfico. Puedes aceptar todas las cookies 
                    o configurar tus preferencias.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={() => setShowSettings(true)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Configurar
              </button>
              <button
                onClick={handleRejectAll}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Rechazar todas
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 transition-colors"
              >
                Aceptar todas
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Cookie Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Configuración de Cookies</h2>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    Gestiona tus preferencias de cookies. Puedes activar o desactivar diferentes tipos de cookies según tus necesidades.
                  </p>
                </div>

                {/* Necessary Cookies */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900">Cookies Necesarias</h3>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={cookiePreferences.necessary}
                        disabled={true}
                        className="h-4 w-4 text-purple-600 rounded border-gray-300 cursor-not-allowed"
                      />
                      <span className="ml-2 text-sm text-gray-500">Siempre activas</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    Estas cookies son esenciales para el funcionamiento del sitio web y no pueden ser desactivadas.
                  </p>
                </div>

                {/* Analytics Cookies */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900">Cookies de Análisis</h3>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={cookiePreferences.analytics}
                        onChange={(e) => handlePreferenceChange('analytics', e.target.checked)}
                        className="h-4 w-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {cookiePreferences.analytics ? 'Activas' : 'Inactivas'}
                      </span>
                    </label>
                  </div>
                  <p className="text-sm text-gray-600">
                    Nos ayudan a entender cómo interactúas con el sitio web para mejorar la experiencia.
                  </p>
                </div>

                {/* Marketing Cookies */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900">Cookies de Marketing</h3>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={cookiePreferences.marketing}
                        onChange={(e) => handlePreferenceChange('marketing', e.target.checked)}
                        className="h-4 w-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {cookiePreferences.marketing ? 'Activas' : 'Inactivas'}
                      </span>
                    </label>
                  </div>
                  <p className="text-sm text-gray-600">
                    Utilizadas para mostrar anuncios relevantes y medir la efectividad de campañas publicitarias.
                  </p>
                </div>

                {/* Functional Cookies */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900">Cookies Funcionales</h3>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={cookiePreferences.functional}
                        onChange={(e) => handlePreferenceChange('functional', e.target.checked)}
                        className="h-4 w-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {cookiePreferences.functional ? 'Activas' : 'Inactivas'}
                      </span>
                    </label>
                  </div>
                  <p className="text-sm text-gray-600">
                    Permiten funcionalidades mejoradas y personalización, como recordar preferencias de idioma.
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowSettings(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCustomSave}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 transition-colors"
                >
                  Guardar Preferencias
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Additional utility component to reset cookies (for testing or admin purposes)
export const CookieDebugger = () => {
  const [showDebugger, setShowDebugger] = useState(false);
  const [cookieData, setCookieData] = useState(null);

  const loadCookieData = () => {
    const consent = localStorage.getItem('vivendius_cookie_consent');
    setCookieData(consent ? JSON.parse(consent) : null);
  };

  const resetCookies = () => {
    localStorage.removeItem('vivendius_cookie_consent');
    setCookieData(null);
    window.location.reload();
  };

  if (process.env.NODE_ENV === 'production') {
    return null; // Don't show in production
  }

  return (
    <div className="fixed top-4 right-4 z-50">
      <button
        onClick={() => {
          setShowDebugger(!showDebugger);
          if (!showDebugger) loadCookieData();
        }}
        className="bg-red-500 text-white px-2 py-1 rounded text-xs font-mono"
      >
        🍪 DEBUG
      </button>
      
      {showDebugger && (
        <div className="absolute top-10 right-0 bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-80 max-h-60 overflow-y-auto">
          <h3 className="font-bold text-sm mb-2">Cookie Debug Info</h3>
          <pre className="text-xs text-gray-600 mb-3">
            {cookieData ? JSON.stringify(cookieData, null, 2) : 'No consent data found'}
          </pre>
          <button
            onClick={resetCookies}
            className="bg-red-500 text-white px-3 py-1 rounded text-xs"
          >
            Reset Cookies
          </button>
        </div>
      )}
    </div>
  );
};

export default CookiesBanner;